import React, { useState } from 'react';
import './RouteInfoTabs.css';

type RouteInfoTabsProps = {
  sourceCityName: string;
  destinationCityName: string;
  inboundCallNumber?: string;
};

const buildTabs = (route: string) => [
  {
    id: 'fare',
    label: 'Fare & Pricing',
    icon: '💰',
    accent: '#d97706',
    summary: `Understand base fare, tolls, GST and driver allowance for ${route} cabs.`
  },
  {
    id: 'vehicle',
    label: 'Vehicle & Luggage',
    icon: '🧳',
    accent: '#2563eb',
    summary: `Compare seating capacity, luggage space and vehicle types for ${route}.`
  },
  {
    id: 'policy',
    label: 'Trip Policy & Timing',
    icon: '⏱️',
    accent: '#7c3aed',
    summary: `Know the timing rules, cancellation terms and waiting policies for ${route} cabs.`
  },
  {
    id: 'route',
    label: 'Route & Stops',
    icon: '📍',
    accent: '#dc6b30',
    summary: `Explore trip types, sightseeing options and en-route stop additions for your ${route} journey.`
  },
  {
    id: 'trust',
    label: 'Trust & Support',
    icon: '🛡️',
    accent: '#0d9488',
    summary: `Travel from ${route} with confidence — verified operators, shared driver details and 24/7 support.`
  }
];

const RouteInfoTabs = ({
  sourceCityName,
  destinationCityName,
  inboundCallNumber
}: RouteInfoTabsProps) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const route = `${sourceCityName} to ${destinationCityName}`;
  const tabs = buildTabs(route);

  const toggleTab = (tabId: string) => {
    setActiveTab(prev => (prev === tabId ? null : tabId));
  };

  return (
    <section className="routeInfoTabs">
      <h2 className="routeInfoTabs__title">{`${sourceCityName} to ${destinationCityName} Cab Booking Guide on MakeMyTrip`}</h2>
      {activeTab && (
        <div className="routeInfoTabs__activeBar">
          <span className="routeInfoTabs__activeLabel">
            {tabs.find(t => t.id === activeTab)?.icon}{' '}
            {tabs.find(t => t.id === activeTab)?.label}
          </span>
          <button
            className="routeInfoTabs__backBtn"
            onClick={() => setActiveTab(null)}
          >
            ← All Topics
          </button>
        </div>
      )}

      <div
        className={`routeInfoTabs__summaries ${
          activeTab ? 'routeInfoTabs__summaries--hidden' : ''
        }`}
      >
        <div className="routeInfoTabs__grid">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className="routeInfoTabs__card"
              style={{ borderTopColor: tab.accent }}
              onClick={() => setActiveTab(tab.id)}
              aria-label={`Know more about ${tab.label}`}
            >
              <span className="routeInfoTabs__cardIcon">{tab.icon}</span>
              <span className="routeInfoTabs__cardLabel">{tab.label}</span>
              <p className="routeInfoTabs__cardSummary">{tab.summary}</p>
              <span
                className="routeInfoTabs__cardCta"
                style={{ color: tab.accent }}
              >
                Know More →
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== 1. Fare & Pricing ===== */}
      <div
        id="tabpanel-fare"
        className={`routeInfoTabs__panel ${
          activeTab === 'fare' ? 'routeInfoTabs__panel--open' : ''
        }`}
      >
        <div className="routeInfoTabs__panelInner">
          <p className="routeInfoTabs__intro">
            Understand the complete fare breakdown for your {route} cab booking.
            Every charge — <strong>base fare</strong>,{' '}
            <strong>per-km rate</strong>, <strong>toll</strong>,{' '}
            <strong>state tax</strong>, <strong>GST</strong>, and
            <strong> driver allowance</strong> — is shown upfront so there are{' '}
            <strong>no surprises</strong> after you book.
          </p>
          <h3 className="routeInfoTabs__subtitle">
            {route} Cab Fare Components
          </h3>
          <div className="routeInfoTabs__tableWrap">
            <table className="routeInfoTabs__table">
              <thead>
                <tr>
                  <th>Fare Component</th>
                  <th>What It Covers</th>
                  <th>When It Applies</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Toll / State Tax / Permit</strong>
                  </td>
                  <td>Road tolls, state entry permits, and e-pass charges</td>
                  <td>To be paid to the driver as actuals during the trip</td>
                </tr>
                <tr>
                  <td>
                    <strong>GST</strong>
                  </td>
                  <td>Goods &amp; Services Tax on the base fare</td>
                  <td>
                    Applied per <strong>government regulation</strong>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Driver Allowance</strong>
                  </td>
                  <td>Food and night-halt allowance for the driver</td>
                  <td>
                    <strong>Always included</strong> in the fare package
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Extra Km Charge</strong>
                  </td>
                  <td>
                    Per-km rate if distance exceeds the{' '}
                    <strong>package limit</strong>
                  </td>
                  <td>Only if actual distance exceeds included kms</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="routeInfoTabs__note">
            Exact toll and state tax amounts depend on the route taken and are
            paid directly during the trip. All other charges are visible in the
            fare summary at the time of booking.
          </p>

          <h3 className="routeInfoTabs__subtitle">
            FAQs — {route} Cab Fare &amp; Charges
          </h3>
          <div className="routeInfoTabs__faqList">
            <div className="routeInfoTabs__faqItem">
              <h4>Is state tax for {route} paid per day or per trip?</h4>
              <p>
                State tax and permit charges are typically applied{' '}
                <strong>per trip</strong> based on the states crossed during the
                journey. For a <strong>round trip</strong>, the tax may apply in
                each direction if the vehicle re-enters the state. The exact
                amount depends on the route and state regulations.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Does GST apply to the base fare or the total including tolls?
              </h4>
              <p>
                <strong>GST</strong> is calculated on the{' '}
                <strong>base fare</strong> of the cab booking. It does not apply
                to toll charges or state taxes, which are collected separately
                as actuals during the trip.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Do I still pay driver allowance if the driver eats with us?
              </h4>
              <p>
                Driver allowance is a <strong>fixed component</strong> included
                in the trip package to cover the driver's food and rest
                expenses. It applies
                <strong> regardless</strong> of whether the driver dines with
                the passengers. This amount is specified in the fare breakdown.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                What is the per-km rate after the included km limit for {route}?
              </h4>
              <p>
                The extra per-km rate is displayed in the fare details at the
                time of booking and varies by cab type. For example, Sedans
                typically have a lower extra-km rate compared to SUVs. Check the
                fare breakup on the booking page for the exact rate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 2. Vehicle & Luggage Capacity ===== */}
      <div
        id="tabpanel-vehicle"
        className={`routeInfoTabs__panel ${
          activeTab === 'vehicle' ? 'routeInfoTabs__panel--open' : ''
        }`}
      >
        <div className="routeInfoTabs__panelInner">
          <p className="routeInfoTabs__intro">
            Choosing the right cab for {route} depends on your{' '}
            <strong>group size</strong>,<strong> luggage count</strong>, and{' '}
            <strong>comfort needs</strong>. Here is a comparison of seating
            capacity, boot space, and suitability for each vehicle type.
          </p>
          <h3 className="routeInfoTabs__subtitle">
            {route} — Vehicle Capacity Guide
          </h3>
          <div className="routeInfoTabs__tableWrap">
            <table className="routeInfoTabs__table">
              <thead>
                <tr>
                  <th>Cab Type</th>
                  <th>Max Passengers</th>
                  <th>Boot Space (Bags)</th>
                  <th>Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Hatchback</strong>
                  </td>
                  <td>
                    Up to <strong>4</strong>
                  </td>
                  <td>Upto 1 medium bag / 2 small bags</td>
                  <td>Solo or couple, light luggage</td>
                </tr>
                <tr>
                  <td>
                    <strong>Sedan</strong> (Dzire / Etios)
                  </td>
                  <td>
                    Up to <strong>4</strong>
                  </td>
                  <td>Upto 1 medium bag / 2 small bags</td>
                  <td>Small family, standard luggage</td>
                </tr>
                <tr>
                  <td>
                    <strong>SUV</strong> (Xylo / Ertiga)
                  </td>
                  <td>
                    Up to <strong>6</strong>
                  </td>
                  <td>Upto 2 medium bags / 3 small bags</td>
                  <td>Groups needing extra legroom</td>
                </tr>

                <tr>
                  <td>
                    <strong>SUV + Roof Carrier</strong>
                  </td>
                  <td>
                    <strong>6</strong>
                  </td>
                  <td>
                    <strong>6+ bags</strong>
                  </td>
                  <td>Groups with heavy or extra luggage</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3 className="routeInfoTabs__subtitle">
            FAQs — Vehicle &amp; Luggage for {route}
          </h3>
          <div className="routeInfoTabs__faqList">
            <div className="routeInfoTabs__faqItem">
              <h4>
                Will 5 adults and 2 large 23 kg bags fit in a Sedan for {route}?
              </h4>
              <p>
                A <strong>Sedan</strong> typically seats up to{' '}
                <strong>4 passengers</strong> comfortably with 2–3 medium
                suitcases. For 5 adults with 2 large bags, an{' '}
                <strong>SUV is recommended</strong> to ensure adequate seating
                and boot space for your {route} journey.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Is a roof carrier available for extra luggage on {route} cab?
              </h4>
              <p>
                <strong>Roof carriers</strong> can be arranged on request for{' '}
                <strong>SUV </strong>
                vehicles, subject to <strong>availability</strong> and vehicle
                type. It is advisable to mention extra luggage needs at the time
                of booking so the appropriate vehicle can be assigned for your{' '}
                {route} trip.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Is there enough legroom in the middle row for tall adults on{' '}
                {route}?
              </h4>
              <p>
                Legroom varies by vehicle model. In most <strong>Sedans</strong>
                , the rear seat is comfortable for average-height adults. For
                taller passengers travelling on {route}, an{' '}
                <strong>SUV like Ertiga or Innova Crysta</strong> offers
                significantly better legroom in the second and third rows.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>Can I request a Diesel vehicle for {route}?</h4>
              <p>
                You can opt for your fuel type preference (
                <strong>Diesel, Petrol, or CNG</strong>) during booking. While
                specific fuel types cannot always be guaranteed, the system will
                try to allocate a matching vehicle based on availability for
                your {route} journey.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Is a child under 5 counted as a full passenger for seating on{' '}
                {route}?
              </h4>
              <p>
                Children <strong>under 5 years</strong> do not require a
                separate seat and are generally <strong>not counted</strong>{' '}
                towards the passenger limit. However, for comfort on long trips
                like {route}, booking a larger vehicle is recommended if
                travelling with infants or toddlers along with additional
                luggage.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 3. Trip Policy & Timing ===== */}
      <div
        id="tabpanel-policy"
        className={`routeInfoTabs__panel ${
          activeTab === 'policy' ? 'routeInfoTabs__panel--open' : ''
        }`}
      >
        <div className="routeInfoTabs__panelInner">
          <p className="routeInfoTabs__intro">
            Know the <strong>timing rules</strong>,{' '}
            <strong>cancellation terms</strong>, and{' '}
            <strong>waiting policies </strong>
            before you travel from {route}. Understanding these details helps
            avoid extra charges and last-minute confusion.
          </p>
          <h3 className="routeInfoTabs__subtitle">
            {route} — Trip Timing &amp; Policy Details
          </h3>
          <div className="routeInfoTabs__tableWrap">
            <table className="routeInfoTabs__table">
              <thead>
                <tr>
                  <th>Policy Area</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Night Charges</strong>
                  </td>
                  <td>
                    Applicable if any part of the trip falls between{' '}
                    <strong>10:00 PM and 6:00 AM</strong>. Applicable for{' '}
                    <strong>round trips</strong> and{' '}
                    <strong>multi-city bookings</strong>. To be paid to the
                    driver as actuals.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Multi-Day Booking</strong>
                  </td>
                  <td>
                    The driver will{' '}
                    <strong>stay with you throughout the trip</strong>, ensuring
                    a smooth journey.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Pickup Time Change</strong>
                  </td>
                  <td>
                    Pickup time can be updated before departure from{' '}
                    <strong>&quot;My Trips&quot;</strong>, subject to driver
                    availability and time-slot constraints.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Waiting During Stops</strong>
                  </td>
                  <td>
                    <strong>No waiting charges</strong> at stops during your
                    trip — the cab stays with you throughout the journey.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Cancellation</strong>
                  </td>
                  <td>
                    <strong>Free cancellation</strong> available till{' '}
                    <strong>1 hour before departure</strong>. Cancellation
                    charges apply after that window.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="routeInfoTabs__subtitle">
            FAQs — Trip Policy for {route}
          </h3>
          <div className="routeInfoTabs__faqList">
            <div className="routeInfoTabs__faqItem">
              <h4>
                If we reach {destinationCityName} after 10 PM due to traffic, do
                I pay night charges?
              </h4>
              <p>
                Night charges apply if any portion of the trip falls between
                <strong> 10:00 PM and 6:00 AM</strong>, regardless of the reason
                for delay. The night charge amount is to be paid to the driver
                as actuals.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                What is the grace period if my flight or train to{' '}
                {sourceCityName} is delayed?
              </h4>
              <p>
                The driver will wait for a considerable duration (usually{' '}
                <strong>30–40 minutes</strong>) at your pickup location. If
                there is a delay beyond this, your trip may be{' '}
                <strong>cancelled without a refund</strong>. If you and the
                driver mutually agree to continue,{' '}
                <strong>waiting charges for every 30 minutes</strong> may apply.
                For more details, please connect with the
                <strong> Trip Assistance Agent</strong>.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Can I change the pickup time a few hours before the {route}{' '}
                trip?
              </h4>
              <p>
                Pickup time can usually be modified before departure, depending
                on driver assignment and availability. Update the time from
                <strong> &quot;My Trips&quot;</strong> or contact support.
                Last-minute changes (<strong>within 1–2 hours of pickup</strong>
                ) may be harder to accommodate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 4. Route & Stops ===== */}
      <div
        id="tabpanel-route"
        className={`routeInfoTabs__panel ${
          activeTab === 'route' ? 'routeInfoTabs__panel--open' : ''
        }`}
      >
        <div className="routeInfoTabs__panelInner">
          <p className="routeInfoTabs__intro">
            Want to explore more on your {route} journey?{' '}
            <strong>Switch your trip type </strong>
            in the search widget to unlock tailored routes, sightseeing options,
            and flexible stop additions. To switch to a{' '}
            <strong>Round Trip</strong>, simply add a return date in the search
            widget. For <strong>en-route stops addition</strong>, add stops
            using the <strong>&quot;Add Stops&quot;</strong> option in the
            search widget.
          </p>

          <h3 className="routeInfoTabs__subtitle">
            Sightseeing Policy — {route}
          </h3>
          <div className="routeInfoTabs__tableWrap">
            <table className="routeInfoTabs__table">
              <thead>
                <tr>
                  <th>Trip Type</th>
                  <th>How to Switch</th>
                  <th>Sightseeing at {destinationCityName}</th>
                  <th>Stops &amp; Local Sightseeing During Journey</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Round Trip</strong>
                  </td>
                  <td>
                    Add a <strong>return date</strong> in the search widget
                  </td>
                  <td>
                    <strong>Included</strong> within package km and hour limits
                  </td>
                  <td>
                    Local sightseeing at {destinationCityName} and en-route
                    stops (temples, viewpoints, restaurants) included. Add stops
                    using <strong>&quot;Add Stops&quot;</strong> in the search
                    widget. Sightseeing at {sourceCityName} is also available as
                    a <strong>special request on the Review page</strong>.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Multi-City</strong>
                  </td>
                  <td>
                    Add stops using <strong>&quot;Add Stops&quot;</strong> in
                    the search widget
                  </td>
                  <td>
                    <strong>Included</strong> within package km and hour limits
                  </td>
                  <td>
                    Sightseeing available at the destination. Add intermediate
                    stops between cities before booking. Sightseeing at{' '}
                    {sourceCityName} is also available as a{' '}
                    <strong>special request on the Review page</strong>.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>One Way</strong>
                  </td>
                  <td>No need to add return date in the search widget</td>
                  <td>
                    <strong>Not available</strong> — covers direct route only
                  </td>
                  <td>
                    No local sightseeing at destination. You can add{' '}
                    <strong>one en-route stop</strong> for a quick halt — ideal
                    for a meal break or a short visit.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="routeInfoTabs__note">
            For round trip and multi-city bookings, the cab stays with you till
            <strong> 9:45 PM</strong> on the last day. Travel beyond 9:45 PM
            will attract <strong>extra night charges</strong>, payable to the
            driver.
          </p>

          <h3 className="routeInfoTabs__subtitle">
            FAQs — Route &amp; Stops for {route}
          </h3>
          <div className="routeInfoTabs__faqList">
            <div className="routeInfoTabs__faqItem">
              <h4>
                Is sightseeing at {destinationCityName} included in my cab
                booking?
              </h4>
              <p>
                Sightseeing at {destinationCityName} is available only for{' '}
                <strong>round trip</strong> bookings, within the included km and
                hour limits. For
                <strong> one-way trips</strong>, sightseeing is{' '}
                <strong>not included</strong> — the cab covers only the direct
                route from {sourceCityName} to {destinationCityName}. If you
                want to explore on a one-way trip, you can add a stop en route.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                How can I add sightseeing at other locations on my {route} trip?
              </h4>
              <p>
                You can explore sightseeing at other spots by adding them as
                stops. Use the <strong>&quot;Add Stops&quot;</strong> button in
                the search widget to include additional locations before
                booking.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Till what time does the cab stay with me on a round trip or
                multi-city trip?
              </h4>
              <p>
                For <strong>round trip and multi-city</strong> bookings, the cab
                stays with you till <strong>9:45 PM</strong> on the last day of
                the trip. If your trip extends beyond 9:45 PM,{' '}
                <strong>extra night charges</strong> are applicable and need to
                be paid directly to the driver.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Can I choose a scenic or alternate route instead of the highway
                for {route}?
              </h4>
              <p>
                You can request an <strong>alternate route</strong> if one is
                available for your trip. If the scenic route is longer than the
                standard highway route, the additional kilometres will be
                charged at the <strong>per-km rate</strong> shown in your
                booking. Discuss the route preference with the driver at pickup.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                Can I add a stop at a popular landmark on the way from{' '}
                {sourceCityName} to {destinationCityName}?
              </h4>
              <p>
                Yes, on-the-way stops at landmarks, temples, or viewpoints are
                allowed. If the stop is on the booked route, there is{' '}
                <strong>no extra distance charge</strong> — only the waiting
                time is counted against your package hours. Use the{' '}
                <strong>&quot;Add Stops&quot;</strong> button in the search
                widget to include it in your booking.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 5. Trust & Support ===== */}
      <div
        id="tabpanel-trust"
        className={`routeInfoTabs__panel ${
          activeTab === 'trust' ? 'routeInfoTabs__panel--open' : ''
        }`}
      >
        <div className="routeInfoTabs__panelInner">
          <p className="routeInfoTabs__intro">
            Travel from {route} with confidence. Cabs are assigned through
            <strong> verified operators</strong>, driver details are shared
            before pickup, and
            <strong> customer support</strong> is available throughout your trip
            for any service-related concern.
          </p>
          <h3 className="routeInfoTabs__subtitle">
            {route} — Safety &amp; Support Overview
          </h3>
          <div className="routeInfoTabs__tableWrap">
            <table className="routeInfoTabs__table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>What to Expect</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Driver Verification</strong>
                  </td>
                  <td>
                    Drivers are assigned through{' '}
                    <strong>verified operators</strong>. Driver details shared
                    via <strong>SMS 30 minutes</strong> before the start of your
                    journey.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Vehicle Condition</strong>
                  </td>
                  <td>Cabs are cleaned and checked before assignment.</td>
                </tr>
                <tr>
                  <td>
                    <strong>AC &amp; Comfort</strong>
                  </td>
                  <td>
                    All listed vehicles are <strong>AC by default</strong>. If
                    the AC is not functioning, contact support for{' '}
                    <strong>immediate assistance</strong>.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Payment &amp; Confirmation</strong>
                  </td>
                  <td>
                    You can choose between <strong>full payment</strong> (total
                    fare at booking) or <strong>part payment</strong> (minimum
                    amount at booking, remaining paid to the driver after the
                    journey). Booking is confirmed after successful payment.
                    Check <strong>&quot;My Trips&quot;</strong> for real-time
                    status.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Ratings &amp; Reviews</strong>
                  </td>
                  <td>
                    Driver and vehicle ratings from past passengers are visible
                    on the review page to help you make an{' '}
                    <strong>informed choice</strong>.
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Contact &amp; Support</strong>
                  </td>
                  <td>
                    Before booking or while making a booking, call{' '}
                    <strong>
                      <a href={`tel:${inboundCallNumber || '01244628747'}`}>
                        {inboundCallNumber || '0124 462 8747'}
                      </a>
                    </strong>
                    . For post-booking support, call{' '}
                    <strong>
                      <a href="tel:01244628747">0124 462 8747</a>
                    </strong>
                    .
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="routeInfoTabs__subtitle">
            FAQs — Trust &amp; Support for {route}
          </h3>
          <div className="routeInfoTabs__faqList">
            <div className="routeInfoTabs__faqItem">
              <h4>
                Can I leave my bags in the car with the driver during a stop on
                the {route} trip?
              </h4>
              <p>
                The driver remains with the vehicle during stops, and the car
                can be locked. However, it is recommended to{' '}
                <strong>carry high-value items</strong> (cameras, laptops,
                wallets) with you. For regular luggage in the boot, the vehicle
                remains <strong>attended by the driver</strong>.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                What if the AC is not working properly during my {route} cab
                trip?
              </h4>
              <p>
                All listed vehicles include{' '}
                <strong>AC as a standard feature</strong>. If the AC is not
                functioning or underperforming during the trip,{' '}
                <strong>contact customer support immediately</strong>. Depending
                on availability, a<strong> replacement vehicle</strong> can be
                arranged.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>How reliable are the driver ratings shown during booking?</h4>
              <p>
                Ratings are based on feedback submitted by past passengers after
                completed trips. A higher rating generally indicates consistent
                service quality. However, newer drivers may have fewer reviews,
                which does not necessarily reflect lower quality.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                I paid for my {route} cab but the app still shows &quot;Payment
                Pending&quot; — what should I do?
              </h4>
              <p>
                If payment has been deducted but the booking status shows
                &quot;Pending,&quot; wait a few minutes for the system to
                update. If it persists,{' '}
                <strong>contact customer support</strong> with your payment
                reference. <strong>Do not make a duplicate payment</strong> —
                the support team can verify and confirm your booking.
              </p>
            </div>
            <div className="routeInfoTabs__faqItem">
              <h4>
                How do I get a replacement if the cab is dirty or in poor
                condition at pickup?
              </h4>
              <p>
                If the vehicle condition at pickup does not meet expectations,
                <strong>
                  {' '}
                  report the issue to customer support immediately
                </strong>
                . Based on availability, a <strong>
                  replacement vehicle
                </strong>{' '}
                can be arranged. It is advisable to check the vehicle before
                starting the trip and raise concerns right away for the fastest
                resolution.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const getRouteInfoTabFaqs = (
  sourceCityName: string,
  destinationCityName: string
): Array<{ question: string; answer: string }> => {
  const route = `${sourceCityName} to ${destinationCityName}`;
  return [
    {
      question: `Is state tax for ${route} paid per day or per trip?`,
      answer: `State tax and permit charges are typically applied per trip based on the states crossed during the journey. For a round trip, the tax may apply in each direction if the vehicle re-enters the state. The exact amount depends on the route and state regulations.`
    },
    {
      question: `Does GST apply to the base fare or the total including tolls?`,
      answer: `GST at 5% is calculated on the base fare of the cab booking. It does not apply to toll charges or state taxes, which are collected separately as actuals during the trip.`
    },
    {
      question: `Do I still pay driver allowance if the driver eats with us?`,
      answer: `Driver allowance is a fixed component included in the trip package to cover the driver's food and rest expenses. It applies regardless of whether the driver dines with the passengers. This amount is specified in the fare breakdown.`
    },
    {
      question: `What is the per-km rate after the included km limit for ${route}?`,
      answer: `The extra per-km rate is displayed in the fare details at the time of booking and varies by cab type. For example, Sedans typically have a lower extra-km rate compared to SUVs. Check the fare breakup on the booking page for the exact rate.`
    },
    {
      question: `Will 5 adults and 2 large 23 kg bags fit in a Sedan for ${route}?`,
      answer: `A Sedan typically seats up to 4 passengers comfortably with 2-3 medium suitcases. For 5 adults with 2 large bags, an SUV is recommended to ensure adequate seating and boot space.`
    },
    {
      question: `Is a roof carrier available for extra luggage on ${route} cab?`,
      answer: `Roof carriers can be arranged on request for SUV vehicles, subject to availability and vehicle type. It is advisable to mention extra luggage needs at the time of booking so the appropriate vehicle can be assigned.`
    },
    {
      question: `Is there enough legroom in the middle row for tall adults?`,
      answer: `Legroom varies by vehicle model. In most Sedans, the rear seat is comfortable for average-height adults. For taller passengers, an SUV like Ertiga or an Innova Crysta offers significantly better legroom in the second and third rows.`
    },
    {
      question: `Can I request a Diesel vehicle for hill routes from ${sourceCityName}?`,
      answer: `You can mention your fuel type preference (Diesel, Petrol, or CNG) during booking. While specific fuel types cannot always be guaranteed, the system will try to allocate a matching vehicle based on availability.`
    },
    {
      question: `Is a child under 5 counted as a full passenger for seating?`,
      answer: `Children under 5 years do not require a separate seat and are generally not counted towards the passenger limit. However, for comfort on long trips like ${route}, booking a larger vehicle is recommended if travelling with infants or toddlers along with additional luggage.`
    },
    {
      question: `If we reach ${destinationCityName} after 10 PM due to traffic, do I pay night charges?`,
      answer: `Night charges apply if any portion of the trip falls between 10:00 PM and 6:00 AM, regardless of the reason for delay. To be paid to the driver as actuals.`
    },
    {
      question: `Can the driver stay with us for 2 full days for local sightseeing near ${destinationCityName}?`,
      answer: `Yes, for multi-day bookings the driver stays with the vehicle throughout the trip. Driver night halt allowance is applicable for each overnight stay and is mentioned in the fare breakdown. Ensure the package hours cover your planned itinerary.`
    },
    {
      question: `What is the grace period if my flight or train to ${sourceCityName} is delayed?`,
      answer: `The driver will wait for a considerable duration (usually 30-40 minutes) at your pickup location. If there is a delay beyond this, your trip may be cancelled without a refund. If you and the driver mutually agree to continue, waiting charges for every 30 minutes may apply. For more details, please connect with the Trip Assistance Agent.`
    },
    {
      question: `Can I change the pickup time a few hours before the ${route} trip?`,
      answer: `Pickup time can usually be modified before departure, depending on driver assignment and availability. Update the time from "My Trips" or contact support. Last-minute changes (within 1-2 hours of pickup) may be harder to accommodate.`
    },
    {
      question: `Is sightseeing at ${destinationCityName} included in my cab booking?`,
      answer: `Sightseeing at ${destinationCityName} is available only for round trip bookings, within the included km and hour limits. For one-way trips, sightseeing is not included — the cab covers only the direct route from ${sourceCityName} to ${destinationCityName}. If you want to explore on a one-way trip, you can add a stop en route.`
    },
    {
      question: `How can I add sightseeing at other locations on my ${route} trip?`,
      answer: `You can explore sightseeing at other spots by adding them as stops. Use the "Add Stops" button in the search widget to include additional locations before booking.`
    },
    {
      question: `Till what time does the cab stay with me on a round trip or multi-city trip?`,
      answer: `For round trip and multi-city bookings, the cab stays with you till 9:45 PM on the last day of the trip. If your trip extends beyond 9:45 PM, extra night charges are applicable and need to be paid directly to the driver.`
    },
    {
      question: `Can I choose a scenic or alternate route instead of the highway for ${route}?`,
      answer: `You can request an alternate route if one is available for your trip. If the scenic route is longer than the standard highway route, the additional kilometres will be charged at the per-km rate shown in your booking. Discuss the route preference with the driver at pickup.`
    },
    {
      question: `Can I add a stop at a popular landmark on the way from ${sourceCityName} to ${destinationCityName}?`,
      answer: `Yes, on-the-way stops at landmarks, temples, or viewpoints are allowed. If the stop is on the booked route, there is no extra distance charge — only the waiting time is counted against your package hours. Use the "Add Stops" button in the search widget to include it in your booking.`
    },
    {
      question: `Can I leave my bags in the car with the driver during a stop on the ${route} trip?`,
      answer: `The driver remains with the vehicle during stops, and the car can be locked. However, it is recommended to carry high-value items (cameras, laptops, wallets) with you. For regular luggage in the boot, the vehicle remains attended by the driver.`
    },
    {
      question: `What if the AC is not working properly during my ${route} cab trip?`,
      answer: `All listed vehicles include AC as a standard feature. If the AC is not functioning or underperforming during the trip, contact customer support immediately. A replacement vehicle can be arranged.`
    },
    {
      question: `How reliable are the driver ratings shown during booking?`,
      answer: `Ratings are based on feedback submitted by past passengers after completed trips. A higher rating generally indicates consistent service quality. However, newer drivers may have fewer reviews, which does not necessarily reflect lower quality.`
    },
    {
      question: `I paid for my ${route} cab but the app still shows "Payment Pending" — what should I do?`,
      answer: `If payment has been deducted but the booking status shows "Pending," wait a few minutes for the system to update. If it persists, contact customer support with your payment reference. Do not make a duplicate payment — the support team can verify and confirm your booking.`
    },
    {
      question: `How do I get a replacement if the cab is dirty or in poor condition at pickup?`,
      answer: `If the vehicle condition at pickup does not meet expectations, report the issue to customer support immediately. Based on availability, a replacement vehicle can be arranged. It is advisable to check the vehicle before starting the trip and raise concerns right away for the fastest resolution.`
    }
  ];
};

export default RouteInfoTabs;
